import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import hospitalsData from '@data/hospitals/hospitals.json';
import Breadcrumb from '@/components/common/Breadcrumb';

const HospitalDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [hospital, setHospital] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const found = hospitalsData.hospitals.find((h) => h.id === id);
    setHospital(found || null);
    setLoading(false);
  }, [id]);

  if (loading) {
    return (
      <div className="container-custom flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!hospital) {
    return (
      <div className="container-custom py-20">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Hospital Not Found</h1>
          <p className="text-gray-600 mb-4">
            The hospital you're looking for doesn't exist.
          </p>
          <Link to="/hospitals" className="btn-primary">
            Back to Hospital Directory
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container-custom">
      <Breadcrumb
        items={[
          { label: 'Home', href: '/' },
          { label: 'Hospitals', href: '/hospitals' },
          { label: hospital.name }
        ]}
      />

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {hospital.images && hospital.images.main ? (
          <img
            src={hospital.images.main}
            alt={hospital.name}
            className="w-full aspect-video object-cover"
          />
        ) : (
          <div className="aspect-video bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center">
            <svg className="w-24 h-24 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
        )}

        <div className="p-8">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <h1 className="text-3xl font-bold text-gray-900">
                  {hospital.name}
                </h1>
                {hospital.certifications.includes('JCI') && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    JCI Certified
                  </span>
                )}
              </div>
              <p className="text-xl text-gray-600 mb-2">{hospital.nameCn}</p>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span className="font-semibold">Class {hospital.level}</span>
                <span>•</span>
                <span>{hospital.city}</span>
                <span>•</span>
                {hospital.rating && (
                  <div className="flex items-center">
                    <svg className="w-4 h-4 text-yellow-400 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    {hospital.rating}/5.0
                  </div>
                )}
              </div>
            </div>

            <div className="mt-4 md:mt-0">
              <Link
                to="/services"
                className="btn-primary"
              >
                Book Appointment
              </Link>
            </div>
          </div>

          <p className="text-gray-700 mb-4">{hospital.description}</p>

          {hospital.website && (
            <a
              href={hospital.website}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium mb-8"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Visit Official Website →
            </a>
          )}

          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-4">International Department</h2>
            {hospital.internationalDepartment && (
              <div>
                <p className="text-gray-700 mb-4">
                  {hospital.internationalDepartment.description}
                </p>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {hospital.internationalDepartment.features.map((feature: string, index: number) => (
                    <li key={index} className="flex items-start">
                      <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-4">Specialties</h2>
            <div className="flex flex-wrap gap-2">
              {hospital.specialties.map((specialty: string) => (
                <span
                  key={specialty}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800"
                >
                  {specialty}
                </span>
              ))}
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-4">Facilities</h2>
            <div className="flex flex-wrap gap-2">
              {hospital.facilities.map((facility: string) => (
                <span
                  key={facility}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800"
                >
                  {facility}
                </span>
              ))}
            </div>
          </div>

          {hospital.images && hospital.images.facilities && hospital.images.facilities.length > 0 && (
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4">Hospital Gallery</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {hospital.images.facilities.map((image: string, index: number) => (
                  <img
                    key={index}
                    src={image}
                    alt={`${hospital.name} facility ${index + 1}`}
                    className="w-full aspect-video object-cover rounded-lg shadow-sm"
                  />
                ))}
              </div>
            </div>
          )}

          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-4">Price Ranges</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Service
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      CNY
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      USD
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {hospital.priceRanges.map((priceRange: any, index: number) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {priceRange.service}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {priceRange.cny}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {priceRange.usd}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-4">Key Doctors</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {hospital.doctors.map((doctor: any, index: number) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-semibold text-lg mb-1">{doctor.name}</h3>
                  <p className="text-sm text-gray-600 mb-2">{doctor.nameCn}</p>
                  <div className="text-sm text-gray-700">
                    <p><span className="font-medium">Specialty:</span> {doctor.specialty}</p>
                    <p><span className="font-medium">Title:</span> {doctor.title}</p>
                    {doctor.experience && <p><span className="font-medium">Experience:</span> {doctor.experience}</p>}
                    {doctor.languages && <p><span className="font-medium">Languages:</span> {doctor.languages.join(', ')}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-bold mb-4">Location</h2>
            <div className="bg-gray-100 rounded-lg p-6">
              <p className="text-gray-700 mb-2">
                <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {hospital.address}
              </p>
              <a
                href={`https://maps.google.com/?q=${hospital.coordinates.lat},${hospital.coordinates.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 text-sm font-medium"
              >
                View on Google Maps →
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HospitalDetail;
